import { useCallback, useEffect, useRef } from "react";

import { generateDefense } from "../../shared/domain/defenseGenerator";
import { newActionId } from "../data/callables";
import type { PregameAction } from "../shared/types/contracts";
import * as intents from "../data/intents";
import { manualAssign, suggestLineupOrder } from "../data/viewModel";
import type { ArchivedGameStats } from "../domain/baseballStats";
import type {
  ActiveGameViewModel,
  PlayerListItem,
  PregameViewModel,
  ScheduleModel,
} from "../shared/types/contracts";
import { responseRevision } from "./commandTypes";
import type { EnqueueCommand, RunCommand } from "./commandTypes";
import type { GameActionCommand } from "./useGameCommands";
import type {
  PregameSchedulePatch,
  RevisionedCommandResult,
  SchedulePatch,
  TeamCommands,
} from "./teamTypes";
import { actionKeys } from "./actionKeys";
import type { UpdateScheduleCommand } from "./useScheduleCommands";
import type { OptimisticPregameController } from "./useOptimisticPregame";
import type { OptimisticPlayerPreferencesController } from "./useOptimisticPlayerPreferences";
import { eventRecovery } from "./useSerializedGameQueue";

const seasonIdFor = (game: ArchivedGameStats): string =>
  game.seasonId ??
  (/^\d{4}-/.test(game.scheduledDate ?? game.date ?? "")
    ? (game.scheduledDate ?? game.date).slice(0, 4)
    : "legacy");

type PregameActionCommand = <Type extends PregameAction["type"]>(
  type: Type,
  payload: Extract<PregameAction, { type: Type }>["payload"],
  key: string,
  expectedRevision?: number,
  chainKey?: string,
  onCommitted?: (revision: number) => void,
) => Promise<boolean>;

const sendPregameAction = intents.sendPregameAction as unknown as (
  teamId: string,
  type: PregameAction["type"],
  payload: PregameAction["payload"],
  expectedRevision?: number,
  actionId?: string,
) => Promise<unknown>;

interface PregameCommandOptions {
  teamId: string;
  players: PlayerListItem[];
  prefs: Record<string, string[]>;
  activePlayers: PlayerListItem[];
  pregame: PregameViewModel;
  game: ActiveGameViewModel | null;
  gameArchive: ArchivedGameStats[];
  scheduleGames: ScheduleModel[];
  activeSeasonId: string | null;
  run: RunCommand;
  enqueue: EnqueueCommand;
  gameAction: GameActionCommand;
  updateSchedule: UpdateScheduleCommand;
  showToast: (message: string) => void;
  project: OptimisticPregameController["project"];
  markCommitted: OptimisticPregameController["markCommitted"];
  rejectProjection: OptimisticPregameController["reject"];
  projectPreference: OptimisticPlayerPreferencesController["projectToggle"];
  preferenceRevision: OptimisticPlayerPreferencesController["currentRevision"];
  markPreferenceCommitted: OptimisticPlayerPreferencesController["markCommitted"];
  rejectPreferenceProjection: OptimisticPlayerPreferencesController["reject"];
  startBarrier: () => {
    actionIds: string[];
    acknowledgement: Promise<void>;
  };
}

const PROJECTED_ACTIONS = new Set<PregameAction["type"]>([
  "setOpponentName",
  "setHomeTeam",
  "setAttendance",
  "setOpenInning",
  "setScheduleDetails",
  "setPitcher",
  "setJerseyHolder",
  "reorderLineup",
  "regenerateDefense",
  "setDefenseAssignment",
]);

const START_DEPENDENCY_ACTIONS = new Set<PregameAction["type"]>([
  ...PROJECTED_ACTIONS,
  "removePlayer",
]);

const STRICT_DEPENDENCY_ACTIONS: Partial<
  Record<PregameAction["type"], ReadonlySet<PregameAction["type"]>>
> = {
  reorderLineup: new Set(["setAttendance"]),
  regenerateDefense: new Set([
    "setAttendance",
    "setPitcher",
    "updatePlayer",
    "removePlayer",
  ]),
  setDefenseAssignment: new Set([
    "setAttendance",
    "regenerateDefense",
    "removePlayer",
  ]),
};

function startFingerprint(pregame: PregameViewModel): string {
  return JSON.stringify({
    seasonId: pregame.seasonId,
    opponentName: pregame.oppName,
    isHome: pregame.isHome,
    openInning: pregame.openInning,
    activePlayerIds: pregame.activeIds,
    lineupPlayerIds: pregame.lineupSlots,
    gamePitcherId: pregame.gamePitcher,
    defense: pregame.defense,
    selectedScheduleId: pregame.selectedScheduleId,
    scheduledDate: pregame.scheduledDate,
    timeSlot: pregame.timeSlot,
    field: pregame.field,
    beerDuty: pregame.beerDuty,
  });
}

export type PregameCommands = Pick<
  TeamCommands,
  | "setTeamName"
  | "setOpponentName"
  | "setHomeTeam"
  | "setScheduleDetails"
  | "setAttendance"
  | "addPlayer"
  | "setPitcher"
  | "setJerseyHolder"
  | "togglePref"
  | "removePlayer"
  | "reorderLineup"
  | "suggestLineup"
  | "regenerate"
  | "setManualAssign"
  | "startGame"
>;

export function usePregameCommands({
  teamId,
  players,
  prefs,
  activePlayers,
  pregame,
  game,
  gameArchive,
  scheduleGames,
  activeSeasonId,
  run,
  enqueue,
  gameAction,
  updateSchedule,
  showToast,
  project,
  markCommitted,
  rejectProjection,
  projectPreference,
  preferenceRevision,
  markPreferenceCommitted,
  rejectPreferenceProjection,
  startBarrier,
}: PregameCommandOptions): PregameCommands {
  const pregameRef = useRef(pregame);
  pregameRef.current = pregame;
  const pendingMutationsRef = useRef(
    new Map<string, { type: PregameAction["type"] }>(),
  );
  const pregameRevisionRef = useRef({
    teamId,
    revision: pregame.revision,
  });

  useEffect(() => {
    if (pregameRevisionRef.current.teamId !== teamId) {
      pregameRevisionRef.current = {
        teamId,
        revision: pregame.revision,
      };
      return;
    }
    pregameRevisionRef.current.revision = Math.max(
      pregameRevisionRef.current.revision,
      pregame.revision,
    );
  }, [teamId, pregame.revision]);

  const pregameAction = useCallback<PregameActionCommand>(
    (type, payload, key, expectedRevision, chainKey, onCommitted) => {
      const actionId = newActionId();
      const action = { type, payload } as PregameAction;
      const dependencyTypes = STRICT_DEPENDENCY_ACTIONS[type];
      const dependsOn = dependencyTypes
        ? [...pendingMutationsRef.current]
            .filter(([, pending]) => dependencyTypes.has(pending.type))
            .map(([pendingActionId]) => pendingActionId)
        : [];
      const projection = PROJECTED_ACTIONS.has(type)
        ? project(actionId, action)
        : { acknowledgement: null };
      const recordCommitted = (committedRevision: number) => {
        if (pregameRevisionRef.current.teamId === teamId) {
          pregameRevisionRef.current.revision = Math.max(
            pregameRevisionRef.current.revision,
            committedRevision,
          );
        }
        markCommitted(actionId, committedRevision);
        onCommitted?.(committedRevision);
      };
      const request = enqueue(
        () => {
          const strict = expectedRevision !== undefined;
          const revision =
            pregameRevisionRef.current.teamId === teamId
              ? pregameRevisionRef.current.revision
              : expectedRevision;
          return run(
            () =>
              sendPregameAction(
                teamId,
                type,
                payload,
                strict ? revision : undefined,
                actionId,
              ),
            (response) => {
              const committedRevision = responseRevision(response);
              if (committedRevision !== null) {
                recordCommitted(committedRevision);
              }
            },
          );
        },
        {
          key,
          ...(chainKey ? { chainKey } : {}),
          intentId: actionId,
          ...(dependsOn.length ? { dependsOn } : {}),
          recovery: eventRecovery(teamId, actionId, recordCommitted),
          ...(projection.acknowledgement
            ? { acknowledgement: projection.acknowledgement }
            : {}),
          onChainFailure: () => rejectProjection(actionId),
        },
      );
      pendingMutationsRef.current.set(actionId, { type });
      const clearPending = () => {
        pendingMutationsRef.current.delete(actionId);
      };
      void request.then(clearPending, clearPending);
      return request;
    },
    [teamId, enqueue, run, project, markCommitted, rejectProjection],
  );

  const selectedSchedule = scheduleGames.find(
    (entry) => entry.id === pregame.selectedScheduleId,
  );
  const revisioned = async (
    command: (onCommitted: (revision: number) => void) => Promise<boolean>,
  ): Promise<RevisionedCommandResult> => {
    let committedRevision: number | null = null;
    const succeeded = await command((revision) => {
      committedRevision = revision;
    });
    return { succeeded, committedRevision };
  };
  const setTeamName = (name: string): Promise<RevisionedCommandResult> =>
    revisioned((onCommitted) =>
      pregameAction(
        "setTeamName",
        { name },
        actionKeys.teamName,
        undefined,
        undefined,
        onCommitted,
      ),
    );
  const setOpponentName = (name: string): Promise<RevisionedCommandResult> => {
    if (game?.status === "in_progress") {
      return revisioned((onCommitted) =>
        gameAction(
          "updateLiveSetup",
          { opponentName: name },
          actionKeys.opponentName,
          onCommitted,
        ),
      );
    }
    if (game) {
      return Promise.resolve({ succeeded: false, committedRevision: null });
    }
    return selectedSchedule
      ? revisioned((onCommitted) =>
          updateSchedule(
            selectedSchedule.id,
            { opponentName: name },
            actionKeys.opponentName,
            onCommitted,
          ),
        )
      : revisioned((onCommitted) =>
          pregameAction(
            "setOpponentName",
            { value: name },
            actionKeys.opponentName,
            undefined,
            undefined,
            onCommitted,
          ),
        );
  };
  const setHomeTeam = (value: boolean): Promise<boolean> =>
    selectedSchedule
      ? updateSchedule(
          selectedSchedule.id,
          { isHome: value },
          actionKeys.homeTeam,
        )
      : pregameAction("setHomeTeam", { value }, actionKeys.homeTeam);
  const setScheduleDetails = (
    patch: PregameSchedulePatch,
  ): Promise<RevisionedCommandResult> =>
    selectedSchedule
      ? revisioned((onCommitted) =>
          updateSchedule(
            selectedSchedule.id,
            patch as SchedulePatch,
            actionKeys.scheduleDetails,
            onCommitted,
          ),
        )
      : revisioned((onCommitted) =>
          pregameAction(
            "setScheduleDetails",
            patch,
            actionKeys.scheduleDetails,
            undefined,
            undefined,
            onCommitted,
          ),
        );
  const setAttendance = (
    playerId: string,
    isActive: boolean,
  ): Promise<boolean> =>
    pregameAction(
      "setAttendance",
      { playerId, isActive },
      actionKeys.attendance(playerId),
      undefined,
      `attendance:${playerId}`,
    );
  const addPlayer = (name: string): Promise<boolean> =>
    pregameAction(
      "addPlayer",
      {
        playerId: crypto.randomUUID(),
        name,
      },
      actionKeys.addPlayer,
    );
  const setPitcher = (playerId: string | null): Promise<boolean> =>
    pregameAction("setPitcher", { playerId }, actionKeys.pitcher(playerId));
  const setJerseyHolder = (playerId: string | null): Promise<boolean> =>
    pregameAction("setJerseyHolder", { playerId }, actionKeys.jersey(playerId));

  const togglePref: TeamCommands["togglePref"] = (playerId, position) => {
    const actionId = newActionId();
    const projection = projectPreference(actionId, playerId, position);
    if (!projection) return Promise.resolve(false);
    let expectedPlayerRevision: number | null = null;
    const recordCommitted = (committedRevision: number) => {
      if (pregameRevisionRef.current.teamId === teamId) {
        pregameRevisionRef.current.revision = Math.max(
          pregameRevisionRef.current.revision,
          committedRevision,
        );
      }
      if (expectedPlayerRevision !== null) {
        markPreferenceCommitted(actionId, expectedPlayerRevision + 1);
      }
    };
    const request = enqueue(
      () => {
        const revision = preferenceRevision(playerId);
        expectedPlayerRevision = revision;
        return run(
          () =>
            sendPregameAction(
              teamId,
              "updatePlayer",
              {
                playerId,
                expectedPlayerRevision: revision,
                positionPreferences: projection.positionPreferences,
              },
              undefined,
              actionId,
            ),
          (response) => {
            const committedRevision = responseRevision(response);
            if (committedRevision !== null) recordCommitted(committedRevision);
          },
        );
      },
      {
        key: actionKeys.preferences(playerId),
        chainKey: `player:${playerId}`,
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId, recordCommitted),
        acknowledgement: projection.acknowledgement,
        onChainFailure: () => rejectPreferenceProjection(actionId),
      },
    );
    pendingMutationsRef.current.set(actionId, { type: "updatePlayer" });
    const clearPending = () => pendingMutationsRef.current.delete(actionId);
    void request.then(clearPending, clearPending);
    return request;
  };

  const removePlayer = (playerId: string): Promise<boolean> => {
    const player = players.find((candidate) => candidate.id === playerId);
    if (!player) return Promise.resolve(false);
    return pregameAction(
      "removePlayer",
      {
        playerId,
        expectedPlayerRevision: player.revision,
      },
      actionKeys.removePlayer(playerId),
      pregame.revision,
    );
  };

  const commitLineup = (
    orderedIds: Array<string | null>,
    key: string,
  ): Promise<boolean> => {
    const playerIds = orderedIds.filter((id): id is string => Boolean(id));
    if (game) {
      return gameAction("updateLiveSetup", { lineupPlayerIds: playerIds }, key);
    }
    return pregameAction(
      "reorderLineup",
      { playerIds },
      key,
      pregame.revision,
      "lineup",
    );
  };
  const reorderLineup = (orderedIds: Array<string | null>): Promise<boolean> =>
    commitLineup(orderedIds, actionKeys.reorderLineup);
  const suggestLineup = (): Promise<boolean> => {
    const playersToOrder = game
      ? game.lineup
          .map(
            (entry): PlayerListItem =>
              players.find((player) => player.id === entry.id) ?? {
                id: entry.id ?? "",
                name: entry.name,
                num: entry.num,
                active: true,
                revision: 0,
                positionPreferences: [],
              },
          )
          .filter((player) => player.id)
      : activePlayers;
    const seasonGames = activeSeasonId
      ? gameArchive.filter(
          (archivedGame) => seasonIdFor(archivedGame) === activeSeasonId,
        )
      : [];
    const order = suggestLineupOrder(playersToOrder, seasonGames);
    if (!order) {
      showToast("No season stats yet — play & save a game first");
      return Promise.resolve(false);
    }
    return commitLineup(order, actionKeys.suggestLineup).then((succeeded) => {
      if (succeeded) {
        showToast(
          "Order suggested from season stats — tweak anything you like",
        );
      }
      return succeeded;
    });
  };

  const regenerate = (): Promise<boolean> => {
    if (game) {
      const input = activePlayers.map((player) => ({
        playerId: player.id,
        name: player.name,
        positionPreferences: player.positionPreferences,
      }));
      return gameAction(
        "updateLiveSetup",
        {
          defense: generateDefense(input, pregame.gamePitcher),
        },
        actionKeys.generateDefense,
      );
    }
    return pregameAction(
      "regenerateDefense",
      {},
      actionKeys.generateDefense,
      pregame.revision,
      "defense",
    );
  };

  const setManualAssign: TeamCommands["setManualAssign"] = (
    inning,
    position,
    playerId,
  ) => {
    if (game) {
      return gameAction(
        "updateLiveSetup",
        {
          defense: manualAssign(
            game.defense,
            inning,
            position,
            playerId,
            pregame.activeIds,
            prefs,
          ),
        },
        actionKeys.defenseAssignment(inning, position),
      );
    }
    const expectedOccupantId =
      pregame.defense?.[inning]?.assignment[position] ?? null;
    return pregameAction(
      "setDefenseAssignment",
      {
        inning,
        position,
        playerId,
        expectedOccupantId,
      },
      actionKeys.defenseAssignment(inning, position),
      pregame.revision,
      `defense:${inning}`,
    );
  };

  const startGame = (): Promise<boolean> => {
    const actionId = newActionId();
    const scheduleBarrier = startBarrier();
    const dependsOn = [...pendingMutationsRef.current]
      .filter(([, pending]) => START_DEPENDENCY_ACTIONS.has(pending.type))
      .map(([pendingActionId]) => pendingActionId)
      .concat(scheduleBarrier.actionIds);
    const expectedSetup = startFingerprint(pregame);
    return enqueue(
      async () => {
        await scheduleBarrier.acknowledgement;
        if (startFingerprint(pregameRef.current) !== expectedSetup) {
          showToast("Setup changed — review it before starting the game");
          return false;
        }
        return run(() =>
          intents.sendStartGame({
            teamId,
            expectedRevision:
              pregameRevisionRef.current.teamId === teamId
                ? pregameRevisionRef.current.revision
                : pregame.revision,
            actionId,
          }),
        );
      },
      {
        key: actionKeys.startGame,
        chainKey: "pregame:start",
        intentId: actionId,
        ...(dependsOn.length ? { dependsOn } : {}),
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };

  return {
    setTeamName,
    setOpponentName,
    setHomeTeam,
    setScheduleDetails,
    setAttendance,
    addPlayer,
    setPitcher,
    setJerseyHolder,
    togglePref,
    removePlayer,
    reorderLineup,
    suggestLineup,
    regenerate,
    setManualAssign,
    startGame,
  };
}
